/**
 * ============================================================
 * People.js — Public People Directory
 * ============================================================
 *
 * Loads:
 *   - All visible people   (GET /api/people)
 *   - Group names          (GET /api/people/groups)
 *
 * Handles:
 *   - Group ordering/display
 *   - Sorting logic (A–Z, alumni by endyear)
 *   - 'Show more alumni' pagination
 **/

import React, { useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import SynlabNavbar from "../components/SynlabNavbar";
import SynlabFooter from "../components/SynlabFooter";
import PersonCard from "../components/PersonCard";

import "./css/Page.css";     // global page layout
import "./css/Subpage.css";  // shared layout for subpages


// ------------------------------------------------------------
// Component
// ------------------------------------------------------------
function People() {
  const [people, setPeople] = useState([]);
  const [groups, setGroups] = useState([]);
  const [alumni, setAlumni] = useState([]);
  const [showMore, setShowMore] = useState(false);
  const ALUMNI_LIMIT = 18;


  // ----------------------------------------------------------
  // Load People + Groups
  // ----------------------------------------------------------
  useEffect(() => {
    async function load() {
      const peopleData = await fetch("/api/people").then(res => res.json());
      const groupData = await fetch("/api/people/groups").then(res => res.json());

      // Store people
      setPeople(peopleData);

      // Group ordering comes from DB table
      // groups = [{ id, name }]
      const orderedGroupNames = groupData
        .map(g => g.name)
        .filter(Boolean);

      setGroups(orderedGroupNames);

      // Alumni group = group_name === "Alumni"
      setAlumni(
        peopleData.filter(p => p.group_name === "Alumni")
      );
    }

    load();
  }, []);


  // ----------------------------------------------------------
  // Sorting Helpers
  // ----------------------------------------------------------

  // Sort A -> Z by lastname
  const sortByLastname = (a, b) => a.lastname.localeCompare(b.lastname);

  // Alumni sort: newest endyear first, then A -> Z
  const sortAlumni = (a, b) => {
    const yearA = parseInt(a.endyear || 0);
    const yearB = parseInt(b.endyear || 0);

    if (yearA !== yearB) return yearB - yearA;
    return (a.lastname || "").localeCompare(b.lastname || "");
  };


  // ----------------------------------------------------------
  // Render
  // ----------------------------------------------------------
  return (
    <div className="page-wrapper">

      {/* ----- NAVBAR ----- */}
      <header className="header">
        <SynlabNavbar />
      </header>

      {/* ----- FLEX COLUMN WRAPPER ----- */}
      <div className="inner-page-wrapper">

        {/* ----- HEADER IMAGE + TITLE OVERLAY ----- */}
        <div className="header-image-container">
          <img
            src="/headers/Header_people.png"
            alt="People Header"
            className="header-image"
          />
          <h1 className="header-title">People</h1>
        </div>

        {/* ----- MAIN CONTENT ----- */}
        <section className="page-content">
          <Container className="page-inner" fluid="lg">

            {groups.map((groupName) => {

              // =====================================================
              // ALUMNI GROUP
              // =====================================================
              if (groupName === "Alumni" && alumni.length > 0) {

                const sortedAlumni = [...alumni].sort(sortAlumni);
                const visibleAlumni = sortedAlumni.slice(
                  0,
                  showMore ? sortedAlumni.length : ALUMNI_LIMIT
                );

                return (
                  <div key={groupName}>
                    <h2 className="page-header">{groupName}</h2>

                    <div className="page-container">
                      {visibleAlumni.map((person) => (
                        <PersonCard key={person.id} person={person} />
                      ))}
                    </div>

                    {alumni.length > ALUMNI_LIMIT && (
                      <button
                        className="show-more-button"
                        onClick={() => setShowMore(!showMore)}
                      >
                        {showMore ? "Show Less" : "Show More"}
                      </button>
                    )}
                  </div>
                );
              }

              // =====================================================
              // ALL OTHER GROUPS
              // =====================================================
              const groupPeople = people
                .filter((p) => p.group_name === groupName)
                .sort(sortByLastname);

              if (groupPeople.length === 0) return null;

              return (
                <div key={groupName}>
                  <h2 className="page-header">{groupName}</h2>
                  <div className="page-container">
                    {groupPeople.map((person) => (
                      <PersonCard key={person.id} person={person} />
                    ))}
                  </div>
                </div>
              );
            })}

          </Container>
        </section>

      </div>

      {/* ----- FOOTER ----- */}
      <SynlabFooter />
    </div>
  );
}

export default People;
